import React, { useEffect } from 'react'
import { shape, arrayOf, string } from 'prop-types'
import { IntlProvider } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import tokens from '@gympass/yoga-tokens'

// eslint-disable-next-line import/extensions
import { SegmentCookieManager } from '@gympass/cookies-consent-manager/dist/index.js'

import env from '@config/env'

import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@material-ui/core/styles'

import useScroll from '@hooks/useScroll'
import useQuery from '@hooks/useQuery'

import {
  utms,
  marketingQueryParams,
  utmMap,
  UTM_KEY_VALUE,
} from '@client/utils/utmHelper'
import {
  insertScripts,
  getInitialCategories,
} from '@client/utils/insertThirdPartyScripts'
import { handleChatbot } from '@client/utils/chatbot'
import handlePathname from '@client/utils/handlePathname'
import { getLanguage } from '@client/utils/cookieConsent'

import { addUtmTracking, addUtmQueryParam } from '@store/ducks/utm'

import Routes from './Router'
import GlobalStyle from './styles'
import './styles/fontsTypeKit/neue-haas-grotesk-display.css'

import 'lazysizes/plugins/attrchange/ls.attrchange'
import 'lazysizes'
import inputOverrides from './styles/theme/input'

const theme = createTheme({
  palette: {
    primary: { main: tokens.colors.vibin },
    secondary: { main: tokens.colors.stamina },
  },
  typography: {
    fontFamily: ['Rubik'],
  },
  overrides: {
    ...inputOverrides,
  },
})

export default function App({ i18n }) {
  const location = useLocation()
  const queryParams = useQuery()
  const dispatch = useDispatch()
  const { country } = handlePathname(location.pathname)

  useScroll()
  insertScripts(country)

  useEffect(() => {
    handleChatbot(location.pathname)
  }, [])

  const mapQueryParams = {
    ...utms,
    ...marketingQueryParams,
  }

  const utmTracking = utmMap({
    data: utms,
    keyType: UTM_KEY_VALUE,
    location,
    queryParams,
  })

  const queryParamsTracking = utmMap({
    data: mapQueryParams,
    location,
    queryParams,
  })

  dispatch(addUtmTracking(queryParamsTracking))
  dispatch(addUtmQueryParam(utmTracking))

  const initialCategories = getInitialCategories(country)

  return (
    <IntlProvider
      locale={i18n.language}
      messages={i18n.messages}
      defaultLocale="pt"
    >
      <MuiThemeProvider theme={theme}>
        <GlobalStyle />
        <Routes />
        <SegmentCookieManager
          initialCategories={initialCategories}
          writeKey={env.segmentId}
          policyVersion={env.cookiePolicyVersion}
          language={getLanguage(country)}
        />
      </MuiThemeProvider>
    </IntlProvider>
  )
}

App.propTypes = {
  i18n: shape({
    language: string,
    message: arrayOf(),
  }).isRequired,
}
